import { useCallback, useEffect, useState } from 'react'
import firebase from '../../firebase/compat'
import { client } from '../../hooks/Client'
import TwoFactorStatusBadge from '../auth/TwoFactorStatusBadge'

// Instellingen → Algemeen: tweestapsverificatie verplicht voor alle teamleden
// (MFA fase 5). De instelling staat server-only in MfaPolicies; de regels —
// lid zijn, 'settings.edit', eerst zelf een authenticator-app — staan in
// functions/mfa/mfaPolicy.js. Deze pagina toont wat terugkomt.
// AI-CONTEXT: See docs/features/mfa-totp.md (fase 5)

/**
 * @typedef {object} MfaPolicy
 * @property {boolean} required
 * @property {number} members - Aantal teamleden
 * @property {number} withoutMfa - Teamleden zonder authenticator-app
 */

/** @param {string} name @returns {Function} callable in europe-west1 */
const callable = (name) => firebase.app().functions('europe-west1').httpsCallable(name)

/**
 * @param {object} props
 * @param {boolean} props.canEdit - Of de gebruiker 'settings.edit' heeft; zonder alleen de stand
 */
const MfaPolicySetting = ({ canEdit }) => {
    const [policy, setPolicy] = useState(/** @type {MfaPolicy | null} */ (null))
    const [confirm, setConfirm] = useState(false)
    const [busy, setBusy] = useState(false)
    const [message, setMessage] = useState('')

    const load = useCallback(async () => {
        const result = await callable('getMfaPolicy')({ client })
        setPolicy(result.data)
    }, [])

    useEffect(() => {
        load().catch((e) => setMessage(`Kon de instelling niet ophalen: ${e.message}`))
    }, [load])

    /** @param {boolean} required */
    const save = async (required) => {
        setBusy(true)
        setMessage('')
        try {
            await callable('setMfaPolicy')({ client, required })
        } catch (e) {
            setMessage(e.message)
            setBusy(false)
            return
        }
        setConfirm(false)
        try {
            await load()
        } catch (e) {
            setMessage(`Opgeslagen, maar de nieuwe stand kon niet worden opgehaald: ${e.message}`)
        } finally {
            setBusy(false)
        }
    }

    const actions = () => {
        if (!canEdit || !policy) return null
        if (policy.required) {
            return <button onClick={() => save(false)} disabled={busy}>Niet meer verplicht</button>
        }
        if (!confirm) {
            return <button onClick={() => setConfirm(true)} disabled={busy}>Verplicht maken</button>
        }
        return (
            <>
                <p>Alle teamleden worden uitgelogd. Wie nog geen authenticator-app heeft, moet er bij de volgende inlog een koppelen.</p>
                <div className='two-factor-actions'>
                    <button onClick={() => save(true)} disabled={busy}>Ja, verplicht maken</button>
                    <button onClick={() => setConfirm(false)} disabled={busy}>Annuleren</button>
                </div>
            </>
        )
    }

    return (
        <div className='dashboard-container two-factor-settings'>
            <div className='two-factor-header'>
                <h2>Tweestapsverificatie verplicht voor alle teamleden</h2>
                {policy && <TwoFactorStatusBadge on={policy.required} />}
            </div>
            {policy && <p>{policy.withoutMfa} van de {policy.members} teamleden hebben nog geen authenticator-app gekoppeld.</p>}
            {actions()}
            {message && <p role='alert'>{message}</p>}
        </div>
    )
}

export default MfaPolicySetting
